import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../lib/api';

export interface Workspace {
  type: 'family' | 'care_home';
  id: string;
  name: string;
  label?: string;
}

interface WorkspaceContextValue {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  switchWorkspace: (id: string) => void;
  refreshWorkspaces: () => Promise<void>;
  updateLabel: (familyId: string, label: string | null) => Promise<void>;
  isPro: boolean;
  loading: boolean;
  effectivePlan: string;
  canManageBilling: boolean;
  planOwnerName: string | null;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState<{
    effectivePlan: string;
    canManageBilling: boolean;
    planOwnerName: string | null;
  }>({ effectivePlan: 'free', canManageBilling: false, planOwnerName: null });

  const loadWorkspaces = useCallback(async () => {
    const [families, careHomes] = await Promise.all([
      api.getFamilies().catch(() => [] as any[]),
      api.getCareHomes().catch(() => [] as any[]),
    ]);
    const all: Workspace[] = [
      ...families.map((f: any) => ({
        type: 'family' as const,
        id: f.id,
        name: f.name,
        label: f.label ?? undefined,
      })),
      ...careHomes.map((c: any) => ({ type: 'care_home' as const, id: c.id, name: c.name })),
    ];
    setWorkspaces(all);
    return all;
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const all = await loadWorkspaces();
        // Restore persisted workspace, validate it's still in list, else use first
        const saved = localStorage.getItem('activeWorkspaceId');
        const valid = saved && all.find((w) => w.id === saved);
        setActiveWorkspaceId(valid ? saved : (all[0]?.id ?? null));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [loadWorkspaces]);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? null;

  useEffect(() => {
    if (!activeWorkspace) return;
    api
      .getWorkspacePlan(activeWorkspace.id, activeWorkspace.type)
      .then((data) =>
        setPlanData({
          effectivePlan: data.effectivePlan,
          canManageBilling: data.canManageBilling,
          planOwnerName: data.ownerName,
        })
      )
      .catch(() => {}); // fail silently — defaults are 'free', false, null
  }, [activeWorkspace?.id]);

  const switchWorkspace = (id: string) => {
    localStorage.setItem('activeWorkspaceId', id);
    setActiveWorkspaceId(id);
  };

  const refreshWorkspaces = useCallback(async () => {
    await loadWorkspaces();
  }, [loadWorkspaces]);

  const updateLabel = useCallback(async (familyId: string, label: string | null) => {
    await api.updateCircleLabel(familyId, label);
    setWorkspaces((prev) =>
      prev.map((w) => (w.id === familyId ? { ...w, label: label ?? undefined } : w))
    );
  }, []);

  const isPro = activeWorkspace?.type === 'care_home';

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        switchWorkspace,
        refreshWorkspaces,
        updateLabel,
        isPro,
        loading,
        effectivePlan: planData.effectivePlan,
        canManageBilling: planData.canManageBilling,
        planOwnerName: planData.planOwnerName,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
